import { useEffect, useState } from 'react';
import {
  Badge, Button, Card, Dialog, DialogPanel, Text, Title,
  Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow,
} from '@tremor/react';
import type { LineDetail } from '../api/types';
import { fetchLineDetail } from '../api/inventory';
import { t } from '../lib/i18n';

const mxn = (n: number) =>
  n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });

interface Props {
  open: boolean;
  line: string | null;
  sid?: number | null;
  showCost: boolean;
  onClose: () => void;
}

export default function LineDetailModal({ open, line, sid, showCost, onClose }: Props) {
  const [detail, setDetail] = useState<LineDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !line) return;
    let alive = true;
    setLoading(true);
    setErr(null);
    setDetail(null);
    fetchLineDetail(line, sid)
      .then((d) => { if (alive) setDetail(d); })
      .catch((e: Error) => { if (alive) setErr(e.message ?? String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, line, sid]);

  if (!line) return null;
  const ld = t.inventory.lineDetail;

  return (
    <Dialog open={open} onClose={onClose} static={true}>
      <DialogPanel>
        <Title>
          <span className="font-mono">{line}</span>
          {detail?.description ? ` — ${detail.description}` : ''}
        </Title>

        {loading && <Text className="mt-4">…</Text>}
        {err && <Text className="mt-4 text-red-600 dark:text-red-400">{err}</Text>}

        {!loading && !err && detail && (
          <>
            {/* Header stats */}
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <span>
                <span className="text-tremor-content">{ld.totalParts}: </span>
                <span className="font-semibold tabular-nums">{detail.total_parts.toLocaleString('es-MX')}</span>
              </span>
              {showCost && detail.total_value_mxn !== undefined && (
                <span>
                  <span className="text-tremor-content">{ld.totalValue}: </span>
                  <span className="font-semibold tabular-nums">{mxn(detail.total_value_mxn)}</span>
                </span>
              )}
            </div>

            {/* Suppliers */}
            <div className="mt-4">
              <Text className="font-medium">{ld.suppliers}</Text>
              {detail.suppliers.length === 0 ? (
                <Text className="text-tremor-content">{ld.noSuppliers}</Text>
              ) : (
                <div className="mt-1 flex flex-wrap gap-2">
                  {detail.suppliers.map((s) => (
                    <Badge key={s.id} color="gray">
                      <span className="font-mono">{s.id}</span>
                      {s.name && s.name !== s.id ? ` · ${s.name}` : ''}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Pcodes */}
            <div className="mt-4">
              <Text className="font-medium">{ld.pcodes}</Text>
              {detail.pcodes.length === 0 ? (
                <Text className="text-tremor-content">{ld.noPcodes}</Text>
              ) : (
                <Card className="mt-1 p-0 overflow-hidden">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>{ld.cols.code}</TableHeaderCell>
                        <TableHeaderCell>{ld.cols.desc}</TableHeaderCell>
                        <TableHeaderCell className="text-right">{ld.cols.parts}</TableHeaderCell>
                        {showCost && (
                          <TableHeaderCell className="text-right">{ld.cols.value}</TableHeaderCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.pcodes.map((p) => (
                        <TableRow key={p.code}>
                          <TableCell className="font-mono">{p.code}</TableCell>
                          <TableCell>{p.desc || '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">{p.parts.toLocaleString('es-MX')}</TableCell>
                          {showCost && (
                            <TableCell className="text-right tabular-nums">
                              {p.value_mxn !== undefined ? mxn(p.value_mxn) : '—'}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </div>
          </>
        )}

        <div className="mt-6 flex justify-end">
          <Button onClick={onClose}>{t.common.close}</Button>
        </div>
      </DialogPanel>
    </Dialog>
  );
}
